import type { Domain, HandleReturnType, StateToMessagesReturnType } from '../../domain.ts';
import type { CommandType, DomainType, MetricsIncomingType, NodeMessage } from '../../node.ts';
import type { DomainInfo } from '../../state/types/domain-registry.ts';
import type { Peer, State } from '../../state/types/state.ts';
import { getLogger } from '../../logger.ts';
import type { Repository, RepositoryFactory } from '../../metrics/core.ts';
import { error, isLocalSource, success } from '../../common/messages.ts';
import { exceptionToReason, type Reason, reason, requestToReason, throwReason } from '../../reason.ts';
import {
    metricsBadIdentity,
    metricsDomainUri,
    metricsFailure,
    metricsUnhandledMessage,
    reasonPeerRemoved
} from './constants.ts';
import { inDomain, joinDomain, leaveDomain } from '../../state/core.ts';
import * as metrics from '../../state/types/metrics.ts';
import { type Draft, produce } from 'immer';
import type { DomainJoin } from '../global/types/messages.ts';
import { byIdInDomainThrows, byIdThrows, bySourceInDomain, peersInDomain } from '../../state/peers.ts';
import type { NodeAddress, PeerId, Source } from '../../state/types/common.ts';
import type { DefineRequest, PublishRequest } from './types/requests.ts';
import { isAllowed } from './filters.ts';
import { IOGateway } from "../../../gateway";

const log = getLogger('gateway.domains.metrics');

// this is list is rtt specific can be reduced in the future
const REQUIRED_PROPERTIES = ['system', 'service', 'instance'];
function checkIdentity(repoIdentity) {
    const missing = REQUIRED_PROPERTIES.find((key) => !(key in repoIdentity));
    if (missing) {
        throwReason(metricsBadIdentity, `Repository is missing required ${missing} property`);
    }
}

function defaultRepositoryId(source: Source, peerIdentity: { system?: string, application?: string, machine?: string, "start-time"?: number, startTime?: number }): IOGateway.Metrics.Identity {
    return {
        machine: peerIdentity.machine,
        system: peerIdentity.system,
        service: peerIdentity.application,
        'start-time': peerIdentity["start-time"] ?? peerIdentity.startTime ?? Date.now()
    };
}

/*Local peer is joining the domain.

  Updates the state and generates messages to all existing peers announcing the new peer*/
export function joinPeer(state: State, source: Source, request: DomainJoin): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, identity: peerIdentity, options} = request;
    const repoId = Object.assign({}, defaultRepositoryId(source, peerIdentity), options, peerIdentity);
    if (inDomain(state, peerId, metrics.metricsDomain)) {
        // the peer is already in the domain
        return [state, [success(metricsDomainUri, source, requestId, peerId)]];
    }
    // we need to join it

    checkIdentity(repoId);
    state = produce(joinDomain(state, peerId, metrics.metricsDomain, undefined), (s: Draft<State>) => {
        const peer = s.peers?.[peerId];
        peer![metrics.metricsDomain] = {restrictions: 'local', repoId};
    });

    return [state, [success(metricsDomainUri, source, requestId, peerId)]];
}

function removePeer(state: State, peer: Peer, reason: Reason): State {
    const peerId = peer.id;
    const repositories = peer[metrics.metricsDomain]?.repos;
    if (repositories) {
        log.info(`stopping metrics publishing for peer ${peerId}, reason: ${JSON.stringify(reason)}`);
        repositories.forEach((r) => {
            r.stop();
        });
    }
    return leaveDomain(state, peerId, metrics.metricsDomain);
}

function leave(state: State, source: Source, request): HandleReturnType {
    const {request_id: requestId, peer_id: peerId} = request;
    const peer = byIdThrows(state, peerId);
    const newState = removePeer(state, peer, requestToReason(request));
    return [newState, [success(metricsDomainUri, source, requestId, peerId)]];
}


function ensureRepositories(state: State, factories: RepositoryFactory[], peerId: PeerId, repoId: IOGateway.Metrics.Identity): [State, readonly Repository[]] {
    const nextState = produce(state, (draft: Draft<State>) => {
        const peer = draft.peers?.[peerId];
        let repositories: Repository[] | undefined;
        if (peer) {
            const domain = peer[metrics.metricsDomain];
            if (domain) {
                repositories = domain.repos;
                if (!repositories) {
                    repositories = domain.repos = factories.map(f => {
                        const repository = f.repository(repoId);
                        repository.start();
                        return repository;
                    });
                    domain.repos = repositories;
                }
            }
        }
    });
    return [nextState, nextState.peers?.[peerId]?.[metrics.metricsDomain]?.repos ?? []];
}

export function defineMetrics(state: State, source: Source, request: DefineRequest, factories: RepositoryFactory[], filters?: IOGateway.MetricFilters): HandleReturnType {
    const {peer_id: peerId, metrics: ms} = request;
    const peer = byIdInDomainThrows(state, peerId, metrics.metricsDomain);
    const repoId: IOGateway.Metrics.Identity = peer[metrics.metricsDomain].repoId;
    const definitions = ms.filter(m => isAllowed(repoId, m.name, filters));

    if (definitions.length > 0) {
        if (log.enabledFor('debug')) {
            log.debug(`publisher ${JSON.stringify(repoId)} adding metrics [${definitions.map(d => d.name).join(',')}]`);
        }
        const [newState, repositories] = ensureRepositories(state, factories, peer.id, repoId);
        repositories.forEach(r => r.add(definitions));
        return [newState, []];
    }
    return [state, []];
}

export function publishMetrics(state: State, source: Source, request: PublishRequest, filters?: IOGateway.MetricFilters): HandleReturnType {
    const {peer_id: peerId, values: vs} = request;
    const peer = byIdInDomainThrows(state, peerId, metrics.metricsDomain);
    const repoId = peer[metrics.metricsDomain].repoId;
    const values = vs.filter(v => isAllowed(repoId, v.name, filters));
    //also log large msg
    if (values.length > 0) {
        const repositories = peer[metrics.metricsDomain].repos;
        if (repositories) {
            repositories.forEach(r => r.publish(values));
        }
    }
    return [state, []];
}

/**
 * Source has disconnected from the system
 */
function sourceRemoved(state: State, source: Source): HandleReturnType {
    if (log.enabledFor('debug')) {
        log.debug(`removing source ${JSON.stringify(source)} from metrics domain`);
    }
    state = bySourceInDomain(state, source, metrics.metricsDomain).reduce((s, p) => {
        return removePeer(s, p, reasonPeerRemoved);
    }, state);
    return [state, []];
}

function handleRequest(state: State, source: Source, request, factories: RepositoryFactory[], filters?: IOGateway.MetricFilters): HandleReturnType {

    switch (request.type) {
        case 'domain/join': {
            return joinPeer(state, source, request);
        }
        case 'domain/leave': {
            return leave(state, source, request);
        }
        case 'commands/source-removed': {
            return sourceRemoved(state, source);
        }
        case 'define': {
            return defineMetrics(state, source, request, factories, filters);
        }
        case 'publish': {
            return publishMetrics(state, source, request, filters);
        }
        default: {
            log.error(`Unhandled message ${JSON.stringify(request)}`);
            return [state, [error(metricsDomainUri, source, request['request_id'] ?? -1, request['peer_id'], reason(metricsUnhandledMessage, `Unhandled message ${JSON.stringify(request)}`))]]
        }
    }
}

class MetricsDomain implements Domain<MetricsIncomingType> {
    constructor(private readonly factories: RepositoryFactory[],
                private readonly filters?: IOGateway.MetricFilters) {
    }
    info(): DomainInfo {
        return {
            uri: metricsDomainUri,
            description: '',
            version: 1
        };
    }

    init(state: State): State {
        return state;
    }

    destroy(state: State): State {
        state = peersInDomain(state, metrics.metricsDomain).reduce((s, p) => {
            return removePeer(s, p, reasonPeerRemoved);
        }, state);

        return state;
    }

    handleMessage(state: State, msg: NodeMessage<CommandType | DomainType | MetricsIncomingType>): HandleReturnType {
        const {source, body} = msg;
        try {
            return handleRequest(state, source, body, this.factories, this.filters);
        } catch (e) {
            if (isLocalSource(source)) {
                return [state, [error(metricsDomainUri, source, body['request_id'], body['peer_id'], exceptionToReason(e, metricsFailure))]]
            } else {
                return [state, []]
            }
        }
    }

    stateToMessages(_state: State, _receiver: NodeAddress): StateToMessagesReturnType {
        return [];
    }

}
export type MetricPublishers = RepositoryFactory[];

export function metricsDomain(factories: MetricPublishers, filters?: IOGateway.MetricFilters): MetricsDomain {
    if (log.enabledFor('debug')) {
        log.debug(`Initializing metrics domain with filters ${filters ? JSON.stringify(filters) : '<none>'}`);
    }
    return new MetricsDomain(factories, filters);
}
